import { useState, Fragment } from "react";
import Papa from "papaparse";
import { ModelResults } from "../domain/types";
import { JsonMap } from "../../common/domain/types";
import { toSigFigs } from "../../../utils/format";
import { useImpactMode } from "../../impact/context/ImpactModeContext";
import { useRangeMode } from "../../impact/context/RangeModeContext";
import { isRangeReady, metricRatios, type ScenariosBlock } from "../../impact/domain/scenarios";

type ResultsViewerProps = {
    modelResults: ModelResults;
    projectName: string;
    lifetimeMultiplier?: number | null;
    lifetimeYears?: number;
    scenarios?: ScenariosBlock;
};

export const ResultsViewer = ({ modelResults, projectName, lifetimeMultiplier, lifetimeYears, scenarios }: ResultsViewerProps) => {
    const [activeTab, setActiveTab] = useState<string>("Emissions");
    const { mode } = useImpactMode();
    const { mode: rangeMode } = useRangeMode();
    // Avoided-emission substances all scale with realized displacement, so a
    // single Floor/Ceiling ratio pair (from the endpoint band) maps each
    // central substance value onto its bracket. See report notes: the contract
    // carries no per-substance bands.
    const rangeRatios =
        rangeMode === "range" && isRangeReady(scenarios)
            ? metricRatios(scenarios, mode === "ops" ? "ops" : "ctg", "dalys")
            : null;
    const showRange = !!rangeRatios;

    const TabButton = ({ label }: { label: string }) => (
        <button
            className={`px-4 py-2 rounded-t-md border-b-2 transition text-sm font-medium ${activeTab === label
                ? "border-quantum-500 text-quantum-400"
                : "border-transparent text-text-muted hover:text-quantum-400 hover:border-quantum-500/50"
                }`}
            onClick={() => setActiveTab(label)}
        >
            {label}
        </button>
    );

    const showLifetime = lifetimeMultiplier != null && lifetimeMultiplier > 0;

    const renderTable = (data: JsonMap | JsonMap[], isEmissions = false) => {
        const keys = Object.keys(Array.isArray(data) ? (data[0] || {}) : data);
        const valueKeyIndex = isEmissions && showLifetime ? keys.indexOf("Value") : -1;
        const rangeKeyIndex = isEmissions && showRange ? keys.indexOf("Value") : -1;

        return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
                <thead className="bg-surface-600">
                    <tr>
                        {keys.map((key, ki) => (
                            <Fragment key={key}>
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider"
                                >
                                    {key === "Value" && isEmissions && showLifetime
                                        ? "Year 1"
                                        : key === "Value" && isEmissions && showRange
                                        ? "Central"
                                        : key.replace(/_/g, " ")}
                                </th>
                                {ki === valueKeyIndex && (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                                        Lifetime{lifetimeYears ? ` (${lifetimeYears}-yr)` : ''}
                                    </th>
                                )}
                                {ki === rangeKeyIndex && (
                                    <>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Floor</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Ceiling</th>
                                    </>
                                )}
                            </Fragment>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-surface-700 divide-y divide-border">
                    {Array.isArray(data) ? (
                        (data as any[]).map((row, idx) => (
                            <tr key={idx} className="hover:bg-surface-600 transition-colors">
                                {Object.entries(row).map(([key, val], i) => (
                                    <Fragment key={i}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                            {typeof val === "number" ? toSigFigs(val as number) : String(val)}
                                        </td>
                                        {i === valueKeyIndex && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                {typeof val === "number" ? toSigFigs((val as number) * lifetimeMultiplier!) : '—'}
                                            </td>
                                        )}
                                        {i === rangeKeyIndex && (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                    {typeof val === "number" ? toSigFigs((val as number) * rangeRatios!.floor) : '—'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                    {typeof val === "number" ? toSigFigs((val as number) * rangeRatios!.ceiling) : '—'}
                                                </td>
                                            </>
                                        )}
                                    </Fragment>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr className="hover:bg-surface-600 transition-colors">
                            {Object.entries(data).map(([key, val]: [string, any], i: number) => (
                                <Fragment key={i}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                        {typeof val === "number" ? toSigFigs(val as number) : String(val)}
                                    </td>
                                    {i === valueKeyIndex && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                            {typeof val === "number" ? toSigFigs((val as number) * lifetimeMultiplier!) : '—'}
                                        </td>
                                    )}
                                    {i === rangeKeyIndex && (
                                        <>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                {typeof val === "number" ? toSigFigs((val as number) * rangeRatios!.floor) : '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                {typeof val === "number" ? toSigFigs((val as number) * rangeRatios!.ceiling) : '—'}
                                            </td>
                                        </>
                                    )}
                                </Fragment>
                            ))}
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
        );
    };

    // Select emissions data based on lens mode.
    // Operational_Analysis = ops-only (cradle-to-grave minus infrastructure).
    // Cradle_to_Grave_Analysis = full cradle-to-grave (with infrastructure).
    const emissionsKey = mode === "ops" ? "Operational_Analysis" : "Cradle_to_Grave_Analysis";
    // Model already applies avoided framing (positive = avoided), no negation needed.
    const emissionsData = modelResults.summary_json?.Emissions?.[emissionsKey];

    const renderActiveSection = () => {
        if (!modelResults) return null;
        const { summary_json } = modelResults;
        switch (activeTab) {
            case "Emissions":
                return emissionsData ? (
                    <Fragment>
                        {Object.entries(emissionsData).map(([cat, data]) => (
                            <div key={cat} className="mb-6">
                                <h5 className="text-md font-medium text-text-primary mb-2">{cat.replace(/_/g, " ")}</h5>
                                {renderTable(data as JsonMap, true)}
                            </div>
                        ))}
                    </Fragment>
                ) : null;
            case "Generation":
                return summary_json.Generation ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-border">
                            <thead className="bg-surface-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                                        Generation Type
                                    </th>
                                    {Object.keys(Object.values(summary_json.Generation)[0] || {}).map((header) => (
                                        <th
                                            key={header}
                                            className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider"
                                        >
                                            {header.replace(/_/g, " ")}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-surface-700 divide-y divide-border">
                                {Object.entries(summary_json.Generation).map(([fuelType, values]) => (
                                    <tr key={fuelType} className="hover:bg-surface-600 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">
                                            {fuelType.replace(/_/g, " ")}
                                        </td>
                                        {Object.values(values as JsonMap).map((val, idx) => (
                                            <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                {typeof val === "number" ? (val as number).toFixed(1) : String(val)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null;
            default:
                return null;
        }
    };

    const handleExportCsv = () => {
        if (!modelResults) return;
        const rows: Record<string, any>[] = [];
        const { summary_json } = modelResults;

        const flattenForCsv = (
            section: string,
            category: string,
            data: JsonMap | JsonMap[]
        ): Record<string, any>[] => {
            if (Array.isArray(data)) {
                return data.map((row) => ({ section, category, ...row }));
            }
            return [{ section, category, ...data }];
        };

        // Export emissions for current lens
        if (emissionsData) {
            const label = mode === "ops" ? "Emissions: Operational Analysis" : "Emissions: Cradle to Grave";
            Object.entries(emissionsData).forEach(([cat, data]) => {
                rows.push(...flattenForCsv(label, cat, data as JsonMap));
            });
        }

        if (summary_json.Generation) {
            Object.entries(summary_json.Generation).forEach(([fuel, data]) => {
                rows.push({ section: "Generation", category: fuel, ...(data as JsonMap) });
            });
        }

        const csv = Papa.unparse(rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${projectName || "results"}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // Determine available tabs
    const availableTabs: string[] = [];
    if (emissionsData) availableTabs.push("Emissions");
    if (modelResults.summary_json.Generation) availableTabs.push("Generation");

    if (!availableTabs.includes(activeTab) && availableTabs.length > 0) setActiveTab(availableTabs[0]);

    return (
        <section className="mt-8 bg-surface-700 p-6 rounded-lg shadow border border-border">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-quantum-400">Air, Soil, Water &amp; Resources</h3>
                <button
                    onClick={handleExportCsv}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-quantum-500/50 rounded text-sm font-medium text-quantum-400 hover:bg-quantum-500/10 transition-colors"
                >
                    Export CSV
                </button>
            </div>
            <div className="border-b border-border mb-4 flex space-x-2">
                {availableTabs.map((tab) => (
                    <TabButton key={tab} label={tab} />
                ))}
            </div>
            {renderActiveSection()}
        </section>
    );
};
